import React from 'react';
import {
  Grid,
  GridNoRecords,
  GridCellProps,
  GridColumn as Column,
  GridDataStateChangeEvent
} from '@progress/kendo-react-grid';
import { useParams } from 'react-router-dom';
import { Loader } from '@progress/kendo-react-indicators';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { State, toDataSourceRequestString } from '@progress/kendo-data-query';

import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../../../../components/grid/grid-tool-bar';
import { formatDateToDDMMYYYY } from '../../../../utils/date';
import { ColumnMenu } from '../../../../components/columnMenu';
import { NoRecords } from '../../../../components/grid/no-records';
import { DefaultGridSettings } from '../../../../constants/grid-settings';
import { useGetEventReportactions } from '../../../../hooks/services/use-reports';

const initialDataState: State = {
  sort: [{ field: 'competency', dir: 'asc' }],
  ...DefaultGridSettings.initialDataState
};

export const ActionPlanReportTab: React.FC = () => {
  const { eventId, detailId } = useParams();

  const [dataState, setDataState] = React.useState<State>(initialDataState);

  const { data, isLoading } = useGetEventReportactions({
    eventId,
    type: 'actions',
    playlistId: detailId,
    params: `${toDataSourceRequestString(dataState)}`
  });

  const gridExportRef = React.useRef<ExcelExport | null>(null);

  const dueDateColumn = (props: GridCellProps) => {
    return (
      <td className={'k-table-td'}>
        {formatDateToDDMMYYYY(props.dataItem.dueDate, true)}
      </td>
    );
  };

  return (
    <div
      className={'pt-2'}
      style={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        width: '100%',
        overflow: 'hidden'
      }}>
      <GridToolBar
        searchPlaceholder={'Search action'}
        columnsToSearch={['competency', 'actionPlan', 'status', 'pic']}
        exportRef={gridExportRef}
        showGridMode={false}
        showCardMode={false}
        showBulkExport={true}
        {...dataState}
        onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
          setDataState(e.dataState);
        }}></GridToolBar>
      <ExcelExport
        data={data?.data ?? []}
        ref={gridExportRef}
        fileName={`action-plan.xlsx`}>
        <Grid
          id={'grid-action-plan'}
          style={{
            flexGrow: 1,
            minHeight: 0,
            height: '100%',
            overflow: 'hidden'
          }}
          pageable={DefaultGridSettings.pagerSettings}
          sortable={true}
          data={data}
          {...dataState}
          onDataStateChange={(e: GridDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}>
          <Column
            field={'competency'}
            title={'Competency'}
            width={250}
            columnMenu={ColumnMenu}
          />
          <Column
            field={'actionPlan'}
            title={'Action'}
            columnMenu={ColumnMenu}
          />
          <Column
            field={'status'}
            title={'Status'}
            width={100}
            columnMenu={ColumnMenu}
          />
          <Column
            field={'totalVote'}
            title={'Total vote'}
            width={130}
            columnMenu={ColumnMenu}
          />
          <Column
            field={'pic'}
            title={'PIC'}
            width={150}
            columnMenu={ColumnMenu}
          />
          <Column
            field={'dueDate'}
            title={'Due date'}
            width={180}
            columnMenu={ColumnMenu}
            cells={{ data: (props) => dueDateColumn(props) }}
          />

          <GridNoRecords>
            {isLoading ? (
              <div
                className={
                  'd-flex flex-column align-items-center gap-2 text-muted'
                }>
                <Loader type={'pulsing'} />
                <span>Loading data</span>
              </div>
            ) : (
              <NoRecords />
            )}
          </GridNoRecords>
        </Grid>
      </ExcelExport>
    </div>
  );
};
